import { BaseSearchComponent } from 'src/app/system-common/base-search/base-search.component';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as Chart from 'chart.js';
import { ContestAndIncentiveDetailsService } from '../services/contest-and-incentive-details.service';
import { HeaderService } from 'src/app/system-common/services/header.service';
import { Router } from '@angular/router';
import { SettingService } from 'src/app/system-common/services/setting.service';
import { OfflineService } from 'src/app/offline/offline.service';

@Component({
  selector: '',
  template: '',
})
export class TrackingDashboardSearchHandler
  extends BaseSearchComponent<ContestAndIncentiveDetailsService>
  implements OnInit
{
  @Input() title: string;
  @Input() iconPath: string;
  @Input() chartId: string;
  @Input() modalId;
  @Input() placeholder: string = 'Select Type';
  @Input() data: any[] = [];
  @Input() fieldMap = {
    json: 7,
    name: 2,
    id: 8,
  };
  @Input() chartOptions;
  @Input() backgroundColor;
  @Output() close = new EventEmitter<void>();

  public validJsonIndexes: number[] = [];
  public validCurrentIndex = 0;
  public uniqueTypes: string[] = [];
  public selectedType = '';
  public name = '';
  public showChart = false;
  private chart: Chart;
  private autoTimer: any;
  public currentSelectedTheme;

  constructor(
    public agentService: ContestAndIncentiveDetailsService,
    public headerService: HeaderService,
    public router: Router,
    public settingService: SettingService,
    public offlineService: OfflineService
  ) {
    super(agentService, router, headerService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.currentSelectedTheme = this.settingService.getSessionStorageValue('currentSelectedTheme');
    if (
      (this.currentSelectedTheme != undefined || this.currentSelectedTheme != null) &&
      this.currentSelectedTheme.endsWith('.css')
    ) {
      this.currentSelectedTheme = this.currentSelectedTheme.replace('.css', '');
    }
  }

  ngOnChanges() {
    if (this.data?.length) {
      this.processData();
      this.startAutoRotation();
    }
  }

  processData() {
    this.validJsonIndexes = [];
    this.uniqueTypes = [];

    this.data.forEach((item, index) => {
      const json = item[this.fieldMap.json];
      const name = item[this.fieldMap.name];

      if (json && this.isJson(json)) {
        const parsed = JSON.parse(json);
        const hasValidAction = Object.values(parsed).some((entry: any) => {
          const action = entry?.ACTION?.[0];
          return action?.['Action Name'] && action?.['Action Value'];
        });

        if (hasValidAction) {
          this.validJsonIndexes.push(index);
          if (name && !this.uniqueTypes.includes(name)) {
            this.uniqueTypes.push(name);
          }
        }
      }
    });

    if (this.validJsonIndexes.length) {
      this.validCurrentIndex = 0;
      this.loadChart(this.validJsonIndexes[0]);
    }
  }

  loadChart(index: number) {
    const record = this.data[index];
    const parsed = JSON.parse(record[this.fieldMap.json]);
    const hasValidAction = Object.values(parsed).some((entry: any) => {
      const action = entry?.ACTION?.[0];
      return action?.['Action Name'] && action?.['Action Value'];
    });

    if (!hasValidAction) {
      console.warn('No valid action found');
      return;
    }

    this.showChart = true;
    this.name = record[this.fieldMap.name];

    const ruleIds: string[] = Object.keys(parsed);
    const criteriaSet = new Set<string>();
    const xAxisLabelMap = new Map<string, string>();
    ruleIds.forEach((ruleId) => {
      const entry = parsed[ruleId];
      const action = entry?.ACTION?.[0];
      const actionName = action?.['Action Name'];
      const actionValue = action?.['Action Value'];

      if (!actionName || !actionValue) return;

      const label = `${actionName} - ${actionValue}`;
      xAxisLabelMap.set(ruleId, label);

      const targetAchievement = entry?.TARGETACHIEVEMENT ?? [];
      targetAchievement.forEach((crit: any) => {
        const critName = crit['Criteria Name'];
        if (critName) criteriaSet.add(critName);
      });
    });

    const filteredRuleIds = Array.from(xAxisLabelMap.keys());
    const criteriaList = Array.from(criteriaSet);
    const colors = this.service.getColor?.() ?? ['#36a2eb', '#4bc0c0', '#ffcd56', '#9966ff', '#ff9f40', '#00a65a'];
    const datasets: any[] = [];

    criteriaList.forEach((crit, idx) => {
      const achievementData = filteredRuleIds.map((rid) => {
        const entry = parsed[rid]?.TARGETACHIEVEMENT?.find((c: any) => c['Criteria Name'] === crit);
        return entry ? Math.min(Number(entry['Achievement Percentage'] ?? 0), 100) : 0;
      });

      datasets.push({
        label: crit,
        data: achievementData,
        backgroundColor: colors[idx % colors.length],
        stack: crit,
        barPercentage: filteredRuleIds.length > 1 ? 0.3 : 0.3,
        categoryPercentage: filteredRuleIds.length > 1 ? 0.6 : 0.2,
      });

      const shortfallData = filteredRuleIds.map((rid) => {
        const entry = parsed[rid]?.TARGETACHIEVEMENT?.find((c: any) => c['Criteria Name'] === crit);
        return entry ? -Math.min(Number(entry['Shortfall Percentage'] ?? 0), 100) : 0; // ?? make negative
      });

      datasets.push({
        label: 'Shortfall',
        data: shortfallData,
        stack: crit,
        backgroundColor: 'rgba(243, 15, 15, 0.8)',
        barPercentage: filteredRuleIds.length > 1 ? 0.3 : 0.3,
        categoryPercentage: filteredRuleIds.length > 1 ? 0.6 : 0.2,
      });
    });

    if (this.chart) this.chart.destroy();

    setTimeout(() => {
      this.chart = new Chart(this.chartId, {
        type: 'bar',
        data: {
          labels: filteredRuleIds.map((rid) => xAxisLabelMap.get(rid)!),
          datasets,
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            yAxes: [
              {
                stacked: true,
                ticks: {
                  beginAtZero: true,
                  max: 100,
                  callback: (v: number) => `${v}`,
                  fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
                  fontFamily: 'montserratregular',
                },
                gridLines: {
                  color: this.currentSelectedTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  zeroLineColor: this.currentSelectedTheme === 'dark' ? '#fff' : 'rgba(10, 10, 10, 0.2)',
                  drawBorder: true,
                  lineWidth: 1,
                },
                scaleLabel: {
                  display: true,
                  labelString: 'Percentage',
                  fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
                  fontFamily: 'montserratregular',
                },
              },
            ],
            xAxes: [
              {
                stacked: true,
                ticks: {
                  autoSkip: false,
                  fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
                  fontFamily: 'montserratregular',
                },
                gridLines: {
                  color: this.currentSelectedTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  zeroLineColor: this.currentSelectedTheme === 'dark' ? '#fff' : 'rgba(10, 10, 10, 0.2)',
                  drawBorder: true,
                  lineWidth: 1,
                },
                scaleLabel: {
                  display: true,
                  labelString: 'Actions',
                  fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
                  fontFamily: 'montserratregular',
                },
              },
            ],
          },
          tooltips: {
            mode: 'nearest',
            intersect: true,
            bodyFontFamily: 'montserratregular',
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
              fontFamily: 'montserratregular',
              filter: function (legendItem, chartData) {
                if (legendItem.text !== 'Shortfall') return true;
                const shortfallIndex = chartData.datasets.findIndex((ds) => ds.label === 'Shortfall');
                return legendItem.datasetIndex === shortfallIndex;
              },
            },
          },
          title: {
            display: true,
            text: 'Achievement and Shortfall by Criteria',
            fontColor: this.currentSelectedTheme == 'dark' ? '#fff' : '#000',
            fontFamily: 'montserratregular',
          },
          onClick: (e, activeEls) => this.onGraphPointClick(activeEls),
          plugins: {
            datalabels: false,
          },
        },
      });
    }, 0);
  }

  isJson(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }

  next() {
    if (this.validCurrentIndex < this.validJsonIndexes.length - 1) {
      this.validCurrentIndex++;
      this.loadChart(this.validJsonIndexes[this.validCurrentIndex]);
    }
  }

  previous() {
    if (this.validCurrentIndex > 0) {
      this.validCurrentIndex--;
      this.loadChart(this.validJsonIndexes[this.validCurrentIndex]);
    }
  }

  applyFilter() {
    const indexes = this.data
      .map((d, i) => ({ d, i }))
      .filter(({ d }) => this.isJson(d[this.fieldMap.json]) && d[this.fieldMap.name] === this.selectedType)
      .map(({ i }) => i);

    this.validJsonIndexes = indexes;
    this.validCurrentIndex = 0;
    this.loadChart(indexes[0]);
    $(`#${this.modalId}`).toggle();
  }

  toggleFilterModal() {
    $(`#${this.modalId}`).toggle();
  }

  onCloseClick() {
    if (this.chart) this.chart.destroy();
    this.close.emit();
  }

  startAutoRotation() {
    if (this.autoTimer) clearInterval(this.autoTimer);
    this.autoTimer = setInterval(() => {
      this.validCurrentIndex = (this.validCurrentIndex + 1) % this.validJsonIndexes.length;
      this.loadChart(this.validJsonIndexes[this.validCurrentIndex]);
    }, 9000);
  }

  onGraphPointClick(activeEls: any[]) {
    if (activeEls && activeEls.length > 0) {
      const fallbackRecord = this.data[this.validJsonIndexes[this.validCurrentIndex]];
      if (fallbackRecord) {
        const incentiveId = fallbackRecord[8];
        this.openDetailScreen(incentiveId);
      }
    }
  }

  openDetailScreen(id: string) {
    this.router.navigate(['/contest-and-incentive-details', id]);
  }

  clearData() {
    this.selectedType = '';

    this.validJsonIndexes = [];
    this.validCurrentIndex = 0;

    this.processData();

    this.startAutoRotation();

    if (this.modalId) {
      $(`#${this.modalId}`).hide();
    }
  }

  ngOnDestroy() {
    if (this.autoTimer) clearInterval(this.autoTimer);
    if (this.chart) this.chart.destroy();
  }
}
